import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { ToastController } from "ionic-angular";
import {
  ClearinghouseDetail,
  RequirementsResponseData,
} from "models/payers/payer";
import { ConfigurationService } from "../configuration/configuration.service";

@Injectable()
export class PayersService {
  private readonly payersUrl: string;
  private readonly headers;

  constructor(
    public http: HttpClient,
    private toastCtrl: ToastController,
    private config: ConfigurationService
  ) {
    this.payersUrl = config.getConfig().apiUrl;
    this.headers = new HttpHeaders().set("Content-Type", "application/json");
  }

  searchPayersFromOS(searchDetails: any) {
    return this.http.post(
      `${this.payersUrl}/payer-functions/search`,
      searchDetails
    );
  }

  savePayerDetails(data: any) {
    let url: string = `${this.payersUrl}/payer-functions/payer`;
    return this.http.post(url, JSON.stringify(data), { headers: this.headers });
  }

  getPayerBenefitCodesPeriods(payerId: string, type: string, subtype: string) {
    let url = `${this.payersUrl}/payer-functions/payer-service-codes/payer/${payerId}/benefitType/${type}/periods`;
    if (subtype) url += `?benefitSubtype=${subtype}`;
    return this.http.get(url);
  }

  getPayerBenefitCodesByPeriod(
    payerId: number,
    type: string,
    subtype: string,
    start: string
  ) {
    let url = `${this.payersUrl}/payer-functions/service-codes/payer/${payerId}/benefitType/${type}/period/${start}`;
    if (subtype) url += `?benefitSubtype=${subtype}`;
    return this.http.get(url);
  }

  postPayerBenefitCodes(body: any) {
    return this.http.post(
      `${this.payersUrl}/payer-functions/service-codes`,
      body
    );
  }

  getCountiesAndCBSAByState(state: string) {
    let url: string = `${this.payersUrl}/payer-functions/counties/state/${state}`;
    return this.http.get(url, { headers: this.headers });
  }

  getLocationByState(state: string) {
    let url: string = `${this.payersUrl}/payer-functions/location/state/${state}`;
    return this.http.get(url, { headers: this.headers });
  }

  postSavePayerRates(body: any) {
    return this.http.post(
      `${this.payersUrl}/payer-functions/payer-rates`,
      body
    );
  }

  getCountiesByCBSA(cbsa: string) {
    let url: string = `${this.payersUrl}/payer-functions/counties/cbsa/${cbsa}`;
    return this.http.get(url, { headers: this.headers });
  }

  getPayerRates(
    payer: string,
    benefitType: string,
    contractPeriod: string,
    benefitSubType: string
  ) {
    let url: string = `${this.payersUrl}/payer-functions/payer-rates/payer/${payer}/benefitType/${benefitType}/period/${contractPeriod}`;
    if (benefitSubType) url += `?benefitSubtype=${benefitSubType}`;
    return this.http.get(url, { headers: this.headers });
  }

  getPayerRatesWithLocality(
    payer: string,
    benefitType: string,
    contractPeriod: string,
    payerType: string,
    localityCode: string,
    benefitSubType?: string
  ) {
    const actualPayerId =
      payerType && payerType.toUpperCase() === "MEDICARE" ? "MEDICARE" : payer;
    let url: string = `${this.payersUrl}/payer-functions/payer-rates/payer/${actualPayerId}/benefitType/${benefitType}/period/${contractPeriod}?localityCode=${localityCode}`;
    if (benefitSubType) url += `&benefitSubtype=${benefitSubType}`;
    return this.http.get(url, { headers: this.headers });
  }

  getPayerRatesPeriods(
    payer: string,
    benefitType: string,
    benefitSubType: string,
    payerType?: string,
    localityCode?: string
  ) {
    const actualPayerId =
      payerType && payerType.toUpperCase() === "MEDICARE" ? "MEDICARE" : payer;
    let url: string = `${this.payersUrl}/payer-functions/payer-rates/payer/${actualPayerId}/benefitType/${benefitType}/periods`;

    const params = [];
    if (localityCode) params.push(`localityCode=${localityCode}`);
    if (benefitSubType) params.push(`benefitSubtype=${benefitSubType}`);

    if (params.length > 0) {
      url += `?${params.join("&")}`;
    }

    return this.http.get(url, { headers: this.headers });
  }

  postPayerEdi(payerId: number, body: any) {
    return this.http.post(
      `${this.payersUrl}/payer-functions/edi/${payerId}`,
      body
    );
  }

  postPayerRequirements(body: any) {
    return this.http.post(
      `${this.payersUrl}/payer-functions/payer-requirements`,
      body
    );
  }

  getPayerRequirements(
    payerId: string,
    benefitType: string,
    startDate: string
  ) {
    let url: string = `${this.payersUrl}/payer-functions/payer-requirements/payer/${payerId}/benefitType/${benefitType}/startDate/${startDate}`;
    return this.http.get<RequirementsResponseData>(url, {
      headers: this.headers,
    });
  }

  getPayerRequirementsPeriods(payer: string, benefitType: string) {
    let url: string = `${this.payersUrl}/payer-functions/payer-requirements/payer/${payer}/benefitType/${benefitType}/periods`;
    return this.http.get(url, { headers: this.headers });
  }

  getPayerBillingOptions(
    payerId: string,
    benefitType: string,
    startDate: string
  ) {
    let url: string = `${this.payersUrl}/payer-functions/payer-billing-options/payer/${payerId}/benefitType/${benefitType}/startDate/${startDate}`;
    return this.http.get<RequirementsResponseData>(url, {
      headers: this.headers,
    });
  }

  postPayerBillingOptions(body: any) {
    return this.http.post(
      `${this.payersUrl}/payer-functions/payer-billing-options`,
      body
    );
  }

  getClearinghouseOptions() {
    let url: string = `${this.payersUrl}/claim-submission/clearinghouse`;
    return this.http.get<ClearinghouseDetail[]>(url, {
      headers: this.headers,
    });
  }

  getClearinghouseApiProfiles() {
    let url: string = `${this.payersUrl}/claim-submission/clearinghouse?api=true`;
    return this.http.get(url, { headers: this.headers });
  }

  // Create/update an SFTP clearinghouse profile (CO-674). Same route the list GET uses;
  // the backend (claim-submission-save-clearinghouse-profiles) treats clearinghouseId===0
  // as a CREATE (it mints the next id from the counter) and stamps tenantId + audit fields
  // from the auth user, so the caller only sends the profile fields.
  saveClearinghouseProfile(body: ClearinghouseDetail) {
    let url: string = `${this.payersUrl}/claim-submission/clearinghouse`;
    return this.http.post<ClearinghouseDetail>(url, body, {
      headers: this.headers,
    });
  }

  getPayerShowSendElectronic(claimType: string, payerIds: string[]) {
    let url: string = `${this.payersUrl}/payer-functions`;
    let payerIdsList = "/payer/";
    for (let payerId of payerIds) {
      let intVal = parseInt(payerId);
      payerIdsList += payerId + ",";
    }
    payerIdsList = payerIdsList.substring(0, payerIdsList.length - 1);
    if (payerIds.length > 0) url += payerIdsList + `/claim-type/${claimType}`;
    return this.http.get(url, {
      headers: this.headers,
    });
  }

  postClearingHouseUpload(body: any) {
    let url: string = `${this.payersUrl}/claim-submission/clearinghouse/upload`;
    return this.http.post(url, body);
  }

  getPayerBillingOptionsPeriods(
    payer: string,
    benefitType: string,
    dataType: string
  ) {
    let url: string = `${this.payersUrl}/payer-functions/payer-periods/payer/${payer}/benefitType/${benefitType}/dataType/${dataType}/periods`;
    return this.http.get(url, { headers: this.headers });
  }

  updatePayerPeriod(body: any) {
    return this.http.post(
      `${this.payersUrl}/payer-functions/update/periods`,
      body
    );
  }

  deletePayerPeriod(
    payerId,
    benefitType,
    benefitSubType,
    localityCode,
    periodStartDate,
    apiPrefix
  ) {
    let url = `${this.payersUrl}/payer-functions/${apiPrefix}/payer/${payerId}/benefitType/${benefitType}/period/${periodStartDate}`;
    if (benefitType == "PALLIATIVE" && localityCode)
      url += `?localityCode=${localityCode}`;
    else if (benefitSubType) url += `?benefitSubtype=${benefitSubType}`;
    return this.http.delete(url);
  }

  countClaimsByContractPeriod(
    payerId: string,
    benefitType: string,
    startDate: string,
    endDate?: string,
    benefitSubtype?: string,
    state?: string
  ) {
    let url: string = `${this.payersUrl}/billing-claims/count-contract-period/${payerId}/benefitType/${benefitType}/${startDate}`;

    const params = [];
    if (endDate) params.push(`endDate=${endDate}`);
    if (benefitSubtype) params.push(`benefitSubtype=${benefitSubtype}`);
    if (state) params.push(`state=${state}`);

    if (params.length > 0) {
      url += `?${params.join("&")}`;
    }

    return this.http.get<number>(url, { headers: this.headers });
  }

  normalizePayerDataForSave(value: string): string {
    if (!value) return "";

    return value.replace(/ /g, "").toUpperCase();
  }
}



// WEBPACK FOOTER //
// ./src/providers/payers-service/payers.service.ts